import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Permission,
  PurchaseStatus,
  ScheduleStatus,
  dayKeyInTimeZone,
  parseDateOnly,
  type OverviewResponse,
} from '@construction/shared';
import { api } from '@/api/client';
import { scheduleApi } from '@/api/endpoints';
import { Alert, Badge, Card, CardHeader, PageHeader, Spinner, StatStrip, type StatItem } from '@/components/ui';
import { activeOn } from '@/features/schedule/gantt-layout';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { useMobileSite } from '@/hooks/useCurrentSite';
import { formatMoney } from '@/lib/format';
import { pathFor, shellRoute } from '@/lib/routes/logic';
import type { RouteId, Shell } from '@/lib/routes/types';
import { usePermissions } from '@/lib/permissions';
import { cn } from '@/lib/cn';

/**
 * 工地概览：网页端 `/sites/:siteId` 和 App 首页上的「工地概览」进的是同一页（CR-044，CR-082 按示例重做）。
 *
 * 管理者早上打开后台想知道三件事：今天谁在工地、有什么等我处理、工地上最近在干什么。所以不做图表，做**待办入口** ——
 * 每个数字都能点进去直接处理。从上到下：
 * 1. **待处理**：四格数字（待复核打卡 / 考勤异常 / 采购待批准 / 待报销），点一格落在对应的页签；
 * 2. **统计条**：今日在岗（带现场人数）、今日日报、本周隐蔽工程、本月采购；
 * 3. 左边**近期隐蔽工程**（带第一张照片的缩略图），右边**今天的进度**（从进度表算）和**最新日报**。
 *
 * **每一格按权限出现**（CR-039）。客户原话「只有 审批采购 ，复核打卡 的人才有待处理事项的显示」。
 * 服务端对看不了的字段给 null（不是 0）：null 的那一格不出现；`pending` 为 null 时整张卡连同「一切正常」都不出现。
 *
 * **两套外壳共用**：链接按当前外壳取（`shellRoute`），不能写死任何一边 —— 写死网页端的，App 里一点就被甩出 App 样式。
 */
export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const toMessage = useErrorMessage();
  const { can } = usePermissions();
  const { pathname } = useLocation();
  const shell: Shell = pathname === '/m' || pathname.startsWith('/m/') ? 'mobile' : 'desktop';

  // 网页端工地由路由决定（CR-008）；App 里跟着在班锁定走（约束 9）
  const { site, siteId, isLoading } = useMobileSite();

  const overview = useQuery({
    queryKey: ['overview', siteId],
    queryFn: () => api.get<OverviewResponse>(`/sites/${siteId}/overview`),
    enabled: siteId !== null,
    // 待办数字要跟得上，别让管理员处理完还看到旧数
    refetchInterval: 60_000,
  });

  /** 这一格点进去去哪。按网页端的路由写，App 里换成对应的那一页 */
  const linkTo = (id: RouteId): { to: string; entry: string } | null => {
    const route = shellRoute(id, shell);
    const to = route ? pathFor(route, siteId) : null;
    return route && to ? { to, entry: route.id } : null;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-20 text-ink-3">
        <Spinner />
      </div>
    );
  }

  const data = overview.data;
  const attendance = linkTo('d.attendance');
  const purchases = linkTo('d.purchases');
  const logs = linkTo('d.logs');
  const hidden = linkTo('d.hiddenWorks');
  const schedule = can(Permission.SCHEDULE_VIEW) ? linkTo('d.schedule') : null;
  const tz = data?.site.timezone ?? site?.timezone ?? 'UTC';
  const money = (cents: number) => formatMoney(cents, i18n.language, data?.site.currency ?? 'USD');

  const stats: StatItem[] = [];
  if (data?.today.onSite != null) {
    stats.push({
      key: 'onSite',
      label: t('overview.onSite'),
      value: (
        <>
          {data.today.onSite}
          {data.today.memberCount != null && (
            <small className="ml-1 text-body font-medium text-ink-3">/ {data.today.memberCount}</small>
          )}
        </>
      ),
      hint: t('overview.onSiteHint', { checked: data.today.checkedInToday ?? 0, total: data.today.memberCount ?? 0 }),
      to: attendance ? `${attendance.to}?view=people` : undefined,
      entry: attendance?.entry,
    });
  }
  // 日报没有审核流（CR-023），所以它不是「待办」，是一个进度指标
  if (data?.today.logs != null) {
    stats.push({ key: 'logs', label: t('overview.logsToday'), value: data.today.logs, to: logs?.to, entry: logs?.entry });
  }
  if (data?.stats.hiddenThisWeek != null) {
    stats.push({ key: 'hidden', label: t('overview.hiddenThisWeek'), value: data.stats.hiddenThisWeek, to: hidden?.to, entry: hidden?.entry });
  }
  if (data?.stats.purchasesThisMonth != null) {
    stats.push({
      key: 'purchases',
      label: t('overview.purchasesThisMonth'),
      value: money(data.stats.purchasesThisMonth.amountCents),
      hint: t('overview.purchasesHint', { count: data.stats.purchasesThisMonth.count }),
      to: purchases ? `${purchases.to}?tab=site&status=${PurchaseStatus.REIMBURSED}` : undefined,
      entry: purchases?.entry,
    });
  }

  const pendingCells = data?.pending
    ? [
        // 考勤两类：待复核、异常，点进去落在对应的页签（CR-052）
        data.pending.punches !== null && attendance && {
          key: 'punches',
          label: t('overview.pendingPunches'),
          count: data.pending.punches,
          to: `${attendance.to}?view=pending`,
          entry: attendance.entry,
        },
        data.pending.anomalies !== null && attendance && {
          key: 'anomalies',
          label: t('overview.pendingAnomalies'),
          count: data.pending.anomalies,
          to: `${attendance.to}?view=anomalies`,
          entry: attendance.entry,
        },
        // 采购两步走：等批的、批了没付钱的各一格，点进去落在对应的页签（CR-049）
        data.pending.purchases !== null && purchases && {
          key: 'purchases',
          label: t('overview.pendingPurchases'),
          count: data.pending.purchases,
          to: `${purchases.to}?tab=site&status=${PurchaseStatus.PENDING}`,
          entry: purchases.entry,
        },
        data.pending.reimbursements !== null && purchases && {
          key: 'reimbursements',
          label: t('overview.pendingReimbursements'),
          count: data.pending.reimbursements,
          to: `${purchases.to}?tab=site&status=${PurchaseStatus.APPROVED}`,
          entry: purchases.entry,
        },
      ].filter(Boolean) as Array<{ key: string; label: string; count: number; to: string; entry: string }>
    : [];

  const side = data && (schedule || data.recentLogs !== null);

  return (
    <>
      <PageHeader title={t('nav.overview')} description={site?.name} />

      {overview.isError && <Alert>{toMessage(overview.error)}</Alert>}

      {data && (
        <div className="flex flex-col gap-4">
          {/* 待办放最上面：这是唯一需要管理员立刻动手的东西 */}
          {data.pending !== null &&
            (data.pending.total > 0 ? (
              <Card className="overflow-hidden">
                <CardHeader
                  title={
                    <span className="inline-flex items-center gap-2">
                      {t('overview.needsAction')}
                      <span className="rounded-full bg-danger-solid px-1.5 text-micro font-semibold leading-[18px] tabular-nums text-white">
                        {data.pending.total}
                      </span>
                    </span>
                  }
                />
                <div className={cn('grid', pendingCells.length >= 4 ? 'grid-cols-2 lg:grid-cols-4' : pendingCells.length === 3 ? 'grid-cols-3' : 'grid-cols-2')}>
                  {pendingCells.map((cell) => (
                    <Link
                      key={cell.key}
                      to={cell.to}
                      data-entry={cell.entry}
                      className="block border-rule px-4 py-3.5 transition-colors hover:bg-surface-2/60 [&:not(:first-child)]:border-l max-lg:[&:nth-child(odd)]:border-l-0 max-lg:[&:nth-child(n+3)]:border-t"
                    >
                      <span className="block text-caption text-ink-3">{cell.label}</span>
                      <span
                        className={cn(
                          'mt-0.5 block text-figure font-semibold leading-tight tabular-nums',
                          cell.count > 0 ? 'text-danger' : 'text-ink-4',
                        )}
                      >
                        {cell.count}
                      </span>
                    </Link>
                  ))}
                </div>
              </Card>
            ) : (
              <Card padding="md">
                <p className="text-body text-ok">{t('overview.allClear')}</p>
              </Card>
            ))}

          {stats.length > 0 && <StatStrip items={stats} />}

          <div className={cn('grid gap-4', side && 'lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]')}>
            {/*
              看得了就一直出现，没有记录也写一句（CR-040 走查）：甲方这类只能看隐蔽工程的角色，
              概览里只有这一格。改之前列表为空时整格不渲染，他登录后落在一页只有标题的空白页上。
            */}
            {data.recentHiddenWorks !== null && (
              <Card className="overflow-hidden">
                <CardHeader
                  title={t('overview.recentHidden')}
                  action={
                    hidden && (
                      <Link to={hidden.to} data-entry={hidden.entry} className="text-caption font-medium text-brand-600 hover:underline">
                        {t('overview.viewAll')}
                      </Link>
                    )
                  }
                />
                {data.recentHiddenWorks.length === 0 && (
                  <p className="px-4 py-4 text-body text-ink-3">{t('overview.noRecentHidden')}</p>
                )}
                <ul>
                  {data.recentHiddenWorks.map((record) => {
                    const row = (
                      <>
                        {record.thumbUrl ? (
                          <img src={record.thumbUrl} alt="" loading="lazy" className="size-11 shrink-0 rounded-lg border border-rule object-cover" />
                        ) : (
                          <span className="size-11 shrink-0 rounded-lg border border-dashed border-rule" />
                        )}
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-body font-semibold text-ink">{record.locationDesc}</span>
                          <span className="mt-0.5 flex items-center gap-1.5 text-caption text-ink-3">
                            <Badge tone="brand">{record.category}</Badge>
                            {t('logs.photoCount', { count: record.photoCount })}
                          </span>
                        </span>
                        <span className="shrink-0 text-right text-caption text-ink-3">
                          <span className="block">{record.userName}</span>
                          <span className="block tabular-nums">
                            {new Date(record.capturedAt).toLocaleString(i18n.language, {
                              timeZone: tz,
                              month: 'numeric',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: false,
                            })}
                          </span>
                        </span>
                      </>
                    );
                    const cls = 'flex items-center gap-3 border-b border-rule px-4 py-2.5 last:border-0';
                    return (
                      <li key={record.id}>
                        {hidden ? (
                          <Link to={hidden.to} data-entry={hidden.entry} className={cn(cls, 'transition-colors hover:bg-surface-2/60')}>
                            {row}
                          </Link>
                        ) : (
                          <div className={cls}>{row}</div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </Card>
            )}

            {side && (
              <div className="flex flex-col gap-4">
                {schedule && siteId !== null && <TodayProgress siteId={siteId} timezone={tz} link={schedule} />}
                {data.recentLogs !== null && (
                  <Card className="overflow-hidden">
                    <CardHeader
                      title={t('overview.latestLogs')}
                      action={
                        logs && (
                          <Link to={logs.to} data-entry={logs.entry} className="text-caption font-medium text-brand-600 hover:underline">
                            {t('overview.all')}
                          </Link>
                        )
                      }
                    />
                    {data.recentLogs.length === 0 && <p className="px-4 py-4 text-body text-ink-3">{t('logs.empty')}</p>}
                    <ul>
                      {data.recentLogs.map((log) => (
                        <li key={log.id} className="border-b border-rule px-4 py-2.5 last:border-0">
                          <span className="block text-caption text-ink-3">
                            <b className="font-semibold text-ink">{log.userName}</b>
                            {' · '}
                            <span className="tabular-nums">
                              {new Date(log.createdAt).toLocaleString(i18n.language, {
                                timeZone: tz,
                                month: 'numeric',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false,
                              })}
                            </span>
                          </span>
                          <span className="mt-0.5 block truncate text-body text-ink-2">{log.excerpt || t('logs.noContent')}</span>
                        </li>
                      ))}
                    </ul>
                  </Card>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * 今天的进度（CR-082）：进度表里今天在做的（不含停工项），再加下一项什么时候开始。
 * 数据和进度表页同一份（CR-080），没发布就不出现这张卡。
 */
function TodayProgress({ siteId, timezone, link }: { siteId: number; timezone: string; link: { to: string; entry: string } }) {
  const { t, i18n } = useTranslation();
  const query = useQuery({
    queryKey: ['schedule', siteId, 'data'],
    queryFn: () => scheduleApi.get(siteId),
  });
  const schedule = query.data?.schedule ?? null;
  if (!schedule || schedule.status !== ScheduleStatus.PUBLISHED) return null;

  const today = dayKeyInTimeZone(new Date(), timezone);
  const doing = activeOn(schedule.tasks, today);
  const upcoming = schedule.tasks
    .filter((task) => task.type !== 'NONWORK' && task.plannedStart > today)
    .sort((a, b) => (a.plannedStart < b.plannedStart ? -1 : 1))[0];
  const md = (key: string) =>
    new Date(`${key}T00:00:00Z`).toLocaleDateString(i18n.language, { timeZone: 'UTC', month: 'numeric', day: 'numeric' });
  const daysUntil = (key: string) => {
    const a = parseDateOnly(today);
    const b = parseDateOnly(key);
    return a === null || b === null ? null : Math.round((b - a) / 86_400_000);
  };

  const rows = [
    ...doing.map((task) => ({ task, note: t('overview.inProgress'), tone: 'brand' as const })),
    ...(upcoming
      ? [
          {
            task: upcoming,
            note: daysUntil(upcoming.plannedStart) === 1 ? t('overview.startsTomorrow') : t('overview.startsIn', { days: daysUntil(upcoming.plannedStart) }),
            tone: 'neutral' as const,
          },
        ]
      : []),
  ];

  return (
    <Card className="overflow-hidden">
      <CardHeader
        title={t('overview.todayProgress')}
        action={
          <Link to={link.to} data-entry={link.entry} className="text-caption font-medium text-brand-600 hover:underline">
            {t('overview.scheduleLink')}
          </Link>
        }
      />
      {rows.length === 0 && <p className="px-4 py-4 text-body text-ink-3">{t('home.scheduleNothingToday')}</p>}
      <ul>
        {rows.map(({ task, note, tone }) => (
          <li key={task.id} className="flex items-start gap-3 border-b border-rule px-4 py-2.5 last:border-0">
            <span className="min-w-0 flex-1">
              <span className="block truncate text-body font-semibold text-ink">{task.name}</span>
              <span className="mt-0.5 block text-caption tabular-nums text-ink-3">
                {task.code} · {md(task.plannedStart)}
                {task.plannedFinish !== task.plannedStart && ` – ${md(task.plannedFinish)}`}
              </span>
            </span>
            <Badge tone={tone}>{note}</Badge>
          </li>
        ))}
      </ul>
    </Card>
  );
}
